import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { markChecklistRunReadMutation } from '@/client/@tanstack/react-query.gen'
import { ChecklistRunDetailsPage } from '@/components/checklist-runs/ChecklistRunDetailsPage'
import { Button } from '@/components/ui/button'
import { fetchChecklistRunDetailsById } from '@/lib/fetch-checklist-run-row'
import { parseChecklistRunsSearch } from '@/lib/checklist-runs-search'

export const Route = createFileRoute('/checklist-runs/$runId')({
  component: ChecklistRunDetailRoute,
})

function ChecklistRunDetailRoute() {
  const { t } = useTranslation()
  const { runId } = Route.useParams()
  const queryClient = useQueryClient()

  const runQuery = useQuery({
    queryKey: ['checklistRunDetails', runId],
    queryFn: ({ signal }) => fetchChecklistRunDetailsById(runId, signal),
    enabled: Boolean(runId),
  })

  const markRead = useMutation({
    ...markChecklistRunReadMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (q) => {
          if (
            !Array.isArray(q.queryKey) ||
            q.queryKey[0] == null ||
            typeof q.queryKey[0] !== 'object' ||
            !('_id' in q.queryKey[0])
          ) {
            return false
          }
          const id = (q.queryKey[0] as { _id?: string })._id
          return id === 'listChecklistRunsPaged' || id === 'getChecklistRunDetails'
        },
      })
      void queryClient.invalidateQueries({ queryKey: ['checklistRunDetails', runId] })
    },
  })

  useEffect(() => {
    markRead.mutate({ path: { run_id: runId } })
  }, [runId, markRead.mutate])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/checklist-runs" search={parseChecklistRunsSearch({})}>
            {t('checklistRuns.backToList')}
          </Link>
        </Button>
      </div>

      {runQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" />
          {t('checklistRuns.loadingRun')}
        </div>
      ) : runQuery.isError ? (
        <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
          <p className="font-medium text-destructive">
            {t('checklistRuns.loadRunError')}
          </p>
          <p className="text-muted-foreground">
            {runQuery.error instanceof Error
              ? runQuery.error.message
              : t('checklistRuns.unexpectedError')}
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/checklist-runs" search={parseChecklistRunsSearch({})}>
              {t('checklistRuns.backToList')}
            </Link>
          </Button>
        </div>
      ) : runQuery.data == null ? (
        <div className="space-y-2 rounded-lg border p-4 text-sm">
          <p className="font-medium">{t('checklistRuns.runNotFound')}</p>
          <p className="text-muted-foreground">
            {t('checklistRuns.runNotFoundHint')}
          </p>
          <p className="text-muted-foreground font-mono text-xs">{runId}</p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/checklist-runs" search={parseChecklistRunsSearch({})}>
              {t('checklistRuns.backToList')}
            </Link>
          </Button>
        </div>
      ) : (
        <ChecklistRunDetailsPage runId={runId} details={runQuery.data} />
      )}
    </div>
  )
}
